import React, { useEffect, useRef, useState } from "react";

import classNames from "classnames";
import { graphql, useStaticQuery } from "gatsby";
import { FormattedMessage, useIntl } from "gatsby-plugin-react-intl";
import Accordion from "react-bootstrap/Accordion";
import Container from "react-bootstrap/Container";
import Nav from "react-bootstrap/Nav";
import Navbar from "react-bootstrap/Navbar";

import { ReactComponent as IconClose18 } from "../../../icons/icon/18/a-icon-close-18.svg";
import { ReactComponent as IconGlobe18 } from "../../../icons/icon/18/a-icon-globe-18.svg";
import { ReactComponent as IconLogin18 } from "../../../icons/icon/18/a-icon-login-18.svg";
import { ReactComponent as IconMenu18 } from "../../../icons/icon/18/a-icon-menu-18.svg";
import { ReactComponent as IconSearchTeaser18 } from "../../../icons/icon/18/a-icon-searchteaser-18.svg";
import KontentItemUniversalNavigation from "../../../templates/kontentItemUniversalNavigation";
import scrollActiveNavItem from "../../../utils/scrollActiveNavItem";
import IconButton from "../../button/iconButton";
import TextLightbox from "../../lightbox/textLightbox";
import Logo from "../../logo/logo";
import Search from "../../search/search";
import MobileNavigation from "../mobileNavigation";
import PrimaryNavigation from "../primaryNavigation";
import SecondaryNavigation from "../secondaryNavigation";
import useNavigationData from "../useNavigationData";

import "./navigation.scss";

const getActiveKey = (navigationData) => {
  let activeKey = "";

  navigationData.elements.elements.value.forEach((item, index) => {
    if (window.location.pathname.includes(item.elements.url_slug.value)) {
      activeKey = `menu${index}`;
    }
  });

  return activeKey;
};

const Navigation = () => {
  const intl = useIntl();
  const data = useNavigationData();

  const allWebsitesOpen = useRef(null);
  const burgerNavigation = useRef(null);
  const scrollMenu = useRef(null);
  const navMenu = useRef(null);

  const [burgerMenuSearching, setBurgerMenuSearching] = useState(false);
  const [burgerNavigationOpen, setBurgerNavigationOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [hasShadow, setHasShadow] = useState(false);
  const [scrollMenuClasses, setScrollMenuClasses] = useState("");

  const toggleSearch = (show = null) => {
    const state = show !== null ? show : !searchOpen;

    setSearchOpen(state);

    if (state) {
      document.body.parentNode.classList.add("html-search--open");
    } else {
      document.body.parentNode.classList.remove("html-search--open");
    }
  };

  const scrollTrigger = () => {
    if (!scrollMenu?.current) return null;

    const target = scrollMenu.current.firstElementChild;

    const classes = [];

    if (target.scrollLeft > 20) {
      classes.push("menu-scrolling--left");
    }

    if (
      target.scrollWidth > target.clientWidth
      && target.scrollLeft + target.clientWidth !== target.scrollWidth
    ) {
      classes.push("menu-scrolling--right");
    }

    const classesString = classes.join(" ");

    if (scrollMenuClasses !== classesString) {
      setScrollMenuClasses(classesString);
    }

    return null;
  };

  const resize = () => {
    toggleSearch(false);
    allWebsitesOpen.current?.close();

    scrollTrigger();
  };

  const toggleBurgerNavigation = (show = null) => {
    const state = show !== null ? show : !burgerNavigationOpen;
    setBurgerNavigationOpen(state);
    setHasShadow(false);

    if (state) {
      document.body.parentNode.classList.add("html-burger-navigation--open");
    } else {
      document.body.parentNode.classList.remove("html-burger-navigation--open");
    }
  };

  useEffect(() => {
    window.addEventListener("resize", resize);

    if (navMenu?.current) {
      navMenu.current.scrollLeft = scrollActiveNavItem(navMenu.current);
    }
    scrollTrigger();

    return () => {
      window.removeEventListener("resize", resize);
    };
  }, []);

  const siteSettings = useStaticQuery(graphql`
    {
      kontentItemSiteSettings(system: {codename: {eq: "site_settings"}}) {
        elements {
          button_external_url {
            value
          }
          button_text {
            value
          }
        }
      }
    }
  `);

  return (
    <div className="navigation container--fullwidth">
      <Container fluid>
        <Navbar expand="md" className="top-navigation nav-audience-utilities d-none d-lg-block">
          <Navbar.Collapse>
            <div className="d-none d-md-flex">
              <a
                href="/"
                aria-label={intl.formatMessage({ id: "navigation.logo_title" })}
                title={intl.formatMessage({ id: "navigation.logo_title" })}
              >
                <Logo breakpoint="lg" />
              </a>
            </div>
            <Nav className="d-flex flex-grow-1">
              <div className="ml-auto d-none d-lg-flex">
                <SecondaryNavigation />
                <Nav.Item className="search-toggle d-none d-lg-inline top-navigation__delimiter">
                  <Nav.Link
                    onClick={() => {
                      toggleSearch();
                    }}
                    title={intl.formatMessage({ id: "navigation.menu.search" })}
                  >
                    <span className="d-none d-xl-inline">
                      <FormattedMessage id="navigation.menu.search" />
                    </span>
                    <IconSearchTeaser18 />
                  </Nav.Link>
                </Nav.Item>
                <button
                  className={classNames("all-websites-toggle top-navigation__delimiter")}
                  onClick={() => {
                    allWebsitesOpen.current?.show();
                  }}
                  type="button"
                  aria-label={intl.formatMessage({ id: "navigation.dkms_websites" })}
                >
                  <span>
                    <FormattedMessage id="navigation.dkms_websites" />
                  </span>
                  <IconGlobe18 />
                </button>
              </div>
            </Nav>
          </Navbar.Collapse>
        </Navbar>

        <div
          className={classNames(
            "main-navigation__container container--fullwidth",
            { "scroll-shadow": hasShadow },
          )}
        >
          <Container fluid className="d-lg-flex">
            <Navbar expand="lg" className="main-navigation">
              <Navbar.Brand className="d-lg-none" href="/" title={intl.formatMessage({ id: "navigation.logo_title" })}>
                <Logo breakpoint="lg" />
              </Navbar.Brand>
              <button
                className={classNames("burger-navigation-toggle d-lg-none text-black", { "burger-navigation--open": burgerNavigationOpen })}
                onClick={() => {
                  toggleBurgerNavigation();
                }}
                type="button"
              >
                {process.env.GATSBY_SITE !== "labs" && (
                  <>{burgerNavigationOpen
                    ? <><FormattedMessage id="navigation.menu.close" /></>
                    : <><FormattedMessage id="navigation.menu.menu" /> </>}
                  </>
                )}
                <>
                  {burgerNavigationOpen ? <IconClose18 /> : <IconMenu18 />}
                </>
              </button>
              <Navbar.Collapse
                className={scrollMenuClasses}
                ref={scrollMenu}
                onScroll={() => scrollTrigger()}
              >
                <Nav ref={navMenu}>
                  <PrimaryNavigation />
                </Nav>
              </Navbar.Collapse>
            </Navbar>
            {
              siteSettings.kontentItemSiteSettings.elements.button_external_url.value
              && siteSettings.kontentItemSiteSettings.elements.button_text.value
              && (
                <div className="ml-auto d-none d-lg-flex align-items-center">
                  <IconButton
                    href={siteSettings.kontentItemSiteSettings.elements.button_external_url.value}
                    iconOnly
                    variant="utility"
                    size="sm"
                  >
                    {siteSettings.kontentItemSiteSettings.elements.button_text.value}
                    <IconLogin18 />
                  </IconButton>
                </div>
              )
            }
          </Container>
        </div>

        {searchOpen && (
          <div className="search-overlay">
            <Search
              onCancel={() => {
                toggleSearch();
              }}
              desktop
              focus
            />
          </div>
        )}

        {burgerNavigationOpen && (
          <div
            className="burger-navigation"
            ref={burgerNavigation}
            onScroll={() => {
              setHasShadow(burgerNavigation?.current?.scrollTop !== 0);
            }}
          >

            <Search
              onFocus={() => {
                setBurgerMenuSearching(true);
              }}
              onBlur={() => {
                setBurgerMenuSearching(false);
              }}
            />

            {!burgerMenuSearching && (
              <Nav>
                <Accordion defaultActiveKey={getActiveKey(data.primaryNavigation)}>
                  <MobileNavigation />
                  <div className="burger-navigation__footer">
                    {
                      siteSettings.kontentItemSiteSettings.elements.button_external_url.value
                      && siteSettings.kontentItemSiteSettings.elements.button_text.value
                      && (
                        <div>
                          <IconButton
                            href={siteSettings.kontentItemSiteSettings
                              .elements.button_external_url.value}
                            iconOnly
                            variant="utility"
                            size="sm"
                          >
                            {siteSettings.kontentItemSiteSettings.elements.button_text.value}
                            <IconLogin18 />
                          </IconButton>
                        </div>
                      )
                    }
                    <div>
                      <button
                        className={classNames("all-websites-toggle top-navigation__delimiter", { "html-all-websites--open": allWebsitesOpen.current })}
                        onClick={() => {
                          allWebsitesOpen.current.show();
                        }}
                        type="button"
                        aria-label={intl.formatMessage({ id: "navigation.dkms_websites" })}
                      >
                        <span>
                          <FormattedMessage id="navigation.dkms_websites" />
                        </span>
                        <IconGlobe18 />
                      </button>
                    </div>
                  </div>
                </Accordion>
              </Nav>
            )}

          </div>
        )}
      </Container>

      <TextLightbox
        ref={allWebsitesOpen}
        title={intl.formatMessage({ id: "navigation.dkms_websites" })}
      >
        <KontentItemUniversalNavigation />
      </TextLightbox>
    </div>
  );
};

export default Navigation;
